import React, { useState } from 'react';
import { useGuitarMatch } from '../../context/GuitarMatchContext';
import { GLOBAL_SEVERITY_MATRIX } from '../../services/severityEngine';
import { APP_CONFIG } from '../../services/config';
import { getDeepSnapshot } from '../../services/testReportService';
import { DETERMINISM_POLICY } from '../../contracts/determinism';

interface TruthPanelProps {
    onClose: () => void;
}

type IntegrityStatusUI = 'UNVERIFIED' | 'VERIFIED_LOCAL' | 'VERIFIED_REMOTE';

export const ProductTruthPanel: React.FC<TruthPanelProps> = ({ onClose }) => {
    const { result, currentLevelId } = useGuitarMatch();
    const [copied, setCopied] = useState(false);

    if (!result) return null;

    const session = result.session;
    const metrics = session.metrics;
    const debug = (metrics.debug || {}) as any;
    const truth = session.truth_report;

    // Not strictly needed yet but kept for parity with your governance architecture
    const activeProfile = GLOBAL_SEVERITY_MATRIX[currentLevelId];

    const healthScore =
        debug.sessionHealth !== undefined ? Math.round(debug.sessionHealth * 100) : 'N/A';

    const confidence = Math.round((truth?.confidence || 0) * 100);
    const verdict = truth?.status || 'UNKNOWN';
    const reasons = truth?.score_reasons || [];

    // ✅ TRUST BOUNDARY: only this field is allowed to drive "certified" claims
    const integrityStatus = (truth?.integrity_status || 'UNVERIFIED') as IntegrityStatusUI;

    const isCertifiedRemote = integrityStatus === 'VERIFIED_REMOTE';
    const isVerifiedLocal = integrityStatus === 'VERIFIED_LOCAL';

    // Display signature for debugging only (never used as proof)
    const signature = truth?.tech_pack?.analysis_hash || 'N/A';

    const detFailed = Boolean((result.audit_trail as any)?.determinism_check_failed);

    const handleCopySnapshot = () => {
        const snapshot = getDeepSnapshot(result, currentLevelId);
        navigator.clipboard.writeText(JSON.stringify(snapshot, null, 2));
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // Score display must respect integrity status
    const displayScore =
        integrityStatus === 'VERIFIED_LOCAL' || integrityStatus === 'VERIFIED_REMOTE'
            ? session.score
            : null;

    const integrityBadge = () => {
        if (isCertifiedRemote) {
            return (
                <span className="px-3 py-1 bg-emerald-500 text-black text-[9px] font-black rounded-full shadow-[0_0_15px_rgba(16,185,129,0.4)]">
                    CLOUD CERTIFIED 🛡️
                </span>
            );
        }
        if (isVerifiedLocal) {
            return (
                <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 text-[9px] font-black rounded-full">
                    VERIFIED LOCAL ✓
                </span>
            );
        }
        return (
            <span className="px-3 py-1 bg-orange-500/20 text-orange-500 border border-orange-500/40 text-[9px] font-black rounded-full">
                UNVERIFIED / INDICATIVE
            </span>
        );
    };

    return (
        <div className="fixed inset-0 z-[50000] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 md:p-8 font-mono animate-fade-in">
            <div className="w-full max-w-5xl h-full max-h-[90vh] border border-white/20 rounded-[32px] bg-[#050505] flex flex-col overflow-hidden shadow-[0_0_100px_rgba(255,255,255,0.05)]">
                <header className="p-6 border-b border-white/10 flex justify-between items-center bg-[#0a0a0a]">
                    <div className="space-y-1">
                        <div className="flex items-center gap-3">
                            <span className="text-2xl">🔬</span>
                            <h2 className="text-xl font-bold text-white uppercase tracking-[0.2em]">
                                GSC Truth Engine
                            </h2>
                            {integrityBadge()}
                        </div>
                        <div className="flex gap-4 text-[10px] text-slate-500 uppercase tracking-widest">
                            <span>Build: {APP_CONFIG.ENGINE_VERSION}</span>
                            <span>Rules: {APP_CONFIG.RULES_VERSION}</span>
                        </div>
                    </div>

                    <div className="flex gap-4">
                        <button
                            onClick={handleCopySnapshot}
                            className={`px-6 py-2 rounded-full text-xs font-black uppercase transition-all flex items-center gap-2 ${
                                copied
                                    ? 'bg-emerald-500 text-white'
                                    : 'bg-white/5 border border-white/20 text-slate-300 hover:bg-white hover:text-black'
                            }`}
                        >
                            {copied ? '✓ SNAPSHOT COPIÉ' : '📥 COPIER DEEP SNAPSHOT'}
                        </button>

                        <button
                            onClick={onClose}
                            className="px-6 py-2 border border-white/20 rounded-full text-xs hover:bg-white hover:text-black transition-colors"
                        >
                            FERMER
                        </button>
                    </div>
                </header>

                <div className="flex-1 overflow-y-auto p-8 grid grid-cols-1 md:grid-cols-2 gap-8 custom-scrollbar">
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-xs font-black text-smart-gold uppercase tracking-[0.4em] mb-4 border-b border-white/10 pb-2">
                                Industrial Telemetry
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                                    <div className="text-[9px] text-slate-500 uppercase">
                                        Signal Confidence
                                    </div>
                                    <div
                                        className={`text-3xl font-bold ${
                                            confidence > 75 ? 'text-emerald-400' : 'text-orange-400'
                                        }`}
                                    >
                                        {confidence}%
                                    </div>
                                </div>

                                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                                    <div className="text-[9px] text-slate-500 uppercase">
                                        Reliability Status
                                    </div>
                                    <div
                                        className={`text-xl font-bold ${
                                            detFailed ? 'text-orange-400' : 'text-emerald-400'
                                        }`}
                                    >
                                        {detFailed ? 'DRIFT DETECTED' : 'STABLE KERNEL'}
                                    </div>
                                    <span className="text-[8px] text-slate-600 uppercase font-black">
                                        Level: {DETERMINISM_POLICY.enforcement_level}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xs font-black text-smart-gold uppercase tracking-[0.4em] mb-4 border-b border-white/10 pb-2">
                                Algorithmic Verdict
                            </h3>

                            <div className="flex items-center gap-4 mb-4">
                                <div
                                    className={`text-4xl font-black italic tracking-tighter ${
                                        verdict === 'OK' ? 'text-emerald-500' : 'text-red-500'
                                    }`}
                                >
                                    {verdict === 'OK' ? 'VALIDATED' : verdict}
                                </div>

                                <div className="text-sm font-bold text-white px-3 py-1 bg-white/10 rounded">
                                    SCORE:{' '}
                                    {displayScore !== null && displayScore !== undefined
                                        ? `${displayScore}/100`
                                        : '—'}
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="text-[9px] text-slate-500 uppercase">
                                    Top 3 Penalties (Impact)
                                </div>
                                {reasons.slice(0, 3).map((r: any, i: number) => (
                                    <div
                                        key={i}
                                        className="flex justify-between p-2 bg-red-900/10 border-l-2 border-red-500"
                                    >
                                        <span className="text-xs text-red-200">{r.label}</span>
                                        <span className="text-xs font-bold text-red-400">
                                            -{r.impact} pts
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div>
                            <h3 className="text-xs font-black text-cyan-400 uppercase tracking-[0.4em] mb-4 border-b border-white/10 pb-2">
                                Determinism Proofs
                            </h3>

                            <div className="p-6 bg-white/5 rounded-2xl border border-white/10 space-y-4">
                                <div className="text-[10px] text-slate-300 italic">
                                    Conformité au contrat de stabilité{' '}
                                    {DETERMINISM_POLICY.deterministic_scope}.
                                </div>

                                <div className="space-y-2">
                                    {DETERMINISM_POLICY.known_sources_of_variance.map((src: string) => (
                                        <div key={src} className="flex items-center gap-2">
                                            <div className="w-1 h-1 bg-slate-600 rounded-full" />
                                            <span className="text-[9px] text-slate-500 uppercase">
                                                Variance acceptée : {src}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-xs font-black text-slate-500 uppercase tracking-[0.4em] mb-4 border-b border-white/10 pb-2">
                                Hardware Footprint
                            </h3>

                            <div className="p-4 bg-black border border-white/10 rounded-xl font-mono text-[10px] text-slate-400 space-y-1">
                                <div className="flex justify-between">
                                    <span>RMS Level:</span>
                                    <span>
                                        {typeof debug.rms_db === 'number'
                                            ? `${debug.rms_db.toFixed(1)} dB`
                                            : '—'}
                                    </span>
                                </div>

                                <div className="flex justify-between">
                                    <span>Stability:</span>
                                    <span>
                                        {typeof debug.stability === 'number'
                                            ? debug.stability.toFixed(2)
                                            : '—'}
                                    </span>
                                </div>

                                <div className="flex justify-between border-t border-white/5 mt-2 pt-2">
                                    <span>Analysis ID:</span>
                                    <span className="text-cyan-400 truncate ml-4 max-w-[150px]">
                                        {signature}
                                    </span>
                                </div>

                                <div className="flex justify-between">
                                    <span>Integrity Status:</span>
                                    <span className="text-slate-300">{integrityStatus}</span>
                                </div>
                            </div>
                        </div>

                        <div className="p-4 bg-white/[0.03] border border-white/10 rounded-xl">
                            <div className="text-[9px] text-slate-500 uppercase mb-1">
                                Session Health
                            </div>
                            <div className="text-2xl font-bold text-white">
                                {healthScore}
                                {healthScore !== 'N/A' ? '%' : ''}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
